import CodeMirror from 'codemirror';
import equal from 'fast-deep-equal';
import WebDemoApi from '../webdemo-api';
import CompletionView from '../view/completion-view';

const IMPORT_NAME = 'import';

/**
 * Autocomplete loading indicator management
 */
class AutocompleteLoadingIndicator {
  constructor() {
    this.widget = null;
    this.position = null;
  }

  show(mirror) {
    // Clear any existing indicator first
    this.clear();
    
    const cursor = mirror.getCursor();
    const indicator = document.createElement('span');
    indicator.className = 'autocomplete-loading-indicator';
    indicator.textContent = '...';
    
    // Store position for cleanup validation
    this.position = { line: cursor.line, ch: cursor.ch };
    
    // Store the indicator element itself (addWidget doesn't return anything)
    this.widget = indicator;
    
    // Add widget at cursor position
    mirror.addWidget(cursor, indicator, false);
  }

  clear() {
    if (this.widget) {
      // Remove the widget by removing its DOM element
      if (this.widget.parentNode) {
        this.widget.parentNode.removeChild(this.widget);
      }
      this.widget = null;
      this.position = null;
    }
  }

  getPosition() {
    return this.position;
  }
}

/**
 * Create hint function for Kotlin autocomplete
 * @param {Object} context - The ExecutableFragment instance context
 * @param {AutocompleteLoadingIndicator} loadingIndicator - Loading indicator instance
 * @returns {Function} The hint function for CodeMirror
 */
function createKotlinHint(context, loadingIndicator) {
  return function hint(mirror, callback) {
    let cur = mirror.getCursor();
    let token = mirror.getTokenAt(cur);
    let code = context.state.folded
      ? context.prefix + mirror.getValue() + context.suffix
      : mirror.getValue();
    let currentCursor = context.state.folded
      ? { line: cur.line + context.prefix.split('\n').length - 1, ch: cur.ch }
      : cur;
    
    // Show loading indicator
    loadingIndicator.show(mirror);
    
    WebDemoApi.getAutoCompletion(
      code,
      currentCursor,
      context.state.compilerVersion,
      context.state.targetPlatform,
      context.state.hiddenDependencies,
      processingCompletionsList,
    );
    let withImports = context.canAddImport;

    function processingCompletionsList(results) {
      const anchorCharPosition = mirror.findWordAt({
        line: cur.line,
        ch: cur.ch,
      }).anchor.ch;
      const headCharPosition = mirror.findWordAt({
        line: cur.line,
        ch: cur.ch,
      }).head.ch;
      const currentSymbol = mirror.getRange(
        { line: cur.line, ch: anchorCharPosition },
        {
          line: cur.line,
          ch: headCharPosition,
        },
      );
      if (results.length === 0 && /^[a-zA-Z]+$/.test(currentSymbol)) {
        // Clear the loading indicator before showing fallback autocomplete
        loadingIndicator.clear();
        CodeMirror.showHint(mirror, CodeMirror.hint.auto, {
          completeSingle: false,
        });
      } else {
        // Provide the results to callback, which will show the hint widget
        // We'll clear the indicator AFTER calling callback to ensure it shows first
        callback({
          list: results.map((result) => {
            if (!withImports) result[IMPORT_NAME] = null;
            return new CompletionView(result);
          }),
          from: { line: cur.line, ch: token.start },
          to: { line: cur.line, ch: token.end },
        });
        // Clear after a tiny delay to ensure the hint widget is rendered first
        setTimeout(() => loadingIndicator.clear(), 0);
      }
    }
  };
}

/**
 * Create import suggestions function
 * @param {Object} context - The ExecutableFragment instance context
 * @returns {Function} The showImportSuggestions function
 */
function createShowImportSuggestions(context) {
  return function showImportSuggestions(mirror) {
    if (context.importsSuggestions.length === 0) return;
    let cur = mirror.getCursor();
    let token = mirror.getTokenAt(cur);
    let interval = {
      start: { line: cur.line, ch: token.start },
      end: { line: cur.line, ch: token.end },
    };
    let results = context.importsSuggestions
      .filter((it) => equal(it.interval, interval))
      .map((it) => it.imports)
      .flat();
    let withImports = context.canAddImport;
    if (results.length !== 0) {
      let options = {
        hint: function () {
          return {
            from: mirror.getDoc().getCursor(),
            to: mirror.getDoc().getCursor(),
            list: results.map((result) => {
              if (!withImports) {
                result[IMPORT_NAME] = null;
              }
              return new CompletionView(result);
            }),
          };
        },
      };
      mirror.showHint(options);
    }
  };
}

/**
 * Setup autocomplete for a CodeMirror instance
 * @param {Object} codemirror - CodeMirror instance
 * @param {Object} context - ExecutableFragment instance context
 * @returns {AutocompleteLoadingIndicator} The loading indicator instance for cleanup
 */
export function setupAutocomplete(codemirror, context) {
  const loadingIndicator = new AutocompleteLoadingIndicator();
  
  // Create the hint function with context
  const hint = createKotlinHint(context, loadingIndicator);
  
  // Create the showImportSuggestions function with context
  const showImportSuggestions = createShowImportSuggestions(context);

  // Register the hint helper
  CodeMirror.registerHelper('hint', 'kotlin', hint);
  CodeMirror.hint.kotlin.async = true;

  // Register autocomplete command
  CodeMirror.commands.autocomplete = (cm) => {
    cm.showHint(cm);
  };

  // Set hint options
  codemirror.setOption('hintOptions', { hint });

  return {
    loadingIndicator,
    showImportSuggestions,
  };
}

export { AutocompleteLoadingIndicator };

